import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./Appointment.css";

// Import Components
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import FilterSection from "./components/FilterSection";
import SidePanel from "./components/SidePanel";
import WorkForm from "./components/WorkForm";
import Calendar from "./components/Calendar";
import Footer from "./components/Footer";
import AddWorkButton from "./components/AddWorkButton";
import Services from "./components/Services";
import Branches from "./components/Branches";
import Clients from "./components/Clients";
import TeamMembers from "./components/TeamMembers";
import Sales from "./components/Sales";
import Reports from "./components/Reports";
import Dashboard from "./components/Dashboard";
import UserManagement from "./components/UserManagement";
import Settings from "./components/Settings";
import WhatsAppInbox from "./components/WhatsAppInbox";
import { apiFetch, clearAuthSession } from "./utils/api";
import { canDo } from "./utils/permissions";
import { ALL_MENU_KEYS, TEAM_MEMBER_DEFAULT_MENUS } from "./constants/menuConfig";
import { getTeamMemberAvatar } from "./utils/teamMemberAvatar";

const SUPPORTED_THEMES = ["dark", "light", "white"];

const normalizeTheme = value => (SUPPORTED_THEMES.includes(value) ? value : "dark");

const getNextTheme = theme => {
  const index = SUPPORTED_THEMES.indexOf(theme);
  if (index < 0) return "dark";
  return SUPPORTED_THEMES[(index + 1) % SUPPORTED_THEMES.length];
};

const THEME_BUTTON_LABELS = {
  dark: "Dark Mode",
  light: "Light Mode",
  white: "White Mode"
};

const toMinutes = value => {
  const input = String(value || "").trim();
  const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(input);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

const addMinutesToTime = (timeText, minutesToAdd) => {
  const baseMinutes = toMinutes(timeText);
  if (baseMinutes == null) return "";
  const total = baseMinutes + Number(minutesToAdd || 0);
  const normalized = ((total % 1440) + 1440) % 1440;
  const hours = String(Math.floor(normalized / 60)).padStart(2, "0");
  const minutes = String(normalized % 60).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const addDaysToIsoDate = (isoDate, daysToAdd) => {
  const base = new Date(`${String(isoDate || "").trim() || new Date().toISOString().slice(0, 10)}T00:00:00`);
  if (Number.isNaN(base.getTime())) return new Date().toISOString().slice(0, 10);
  base.setDate(base.getDate() + Number(daysToAdd || 0));
  return base.toISOString().slice(0, 10);
};

const toTimeText = totalMinutes => {
  const normalized = ((Number(totalMinutes) % 1440) + 1440) % 1440;
  const hours = String(Math.floor(normalized / 60)).padStart(2, "0");
  const minutes = String(normalized % 60).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const DEFAULT_BRANCH_WORKING_START = "09:00";
const DEFAULT_BRANCH_WORKING_END = "18:00";

const getBranchWorkingWindow = branch => {
  const startMinutes = toMinutes(branch?.workingStartTime);
  const endMinutes = toMinutes(branch?.workingEndTime);
  const fallbackStart = toMinutes(DEFAULT_BRANCH_WORKING_START);
  const fallbackEnd = toMinutes(DEFAULT_BRANCH_WORKING_END);

  if (startMinutes == null || endMinutes == null || endMinutes <= startMinutes) {
    return {
      startMinutes: fallbackStart,
      endMinutes: fallbackEnd,
      workingStartTime: DEFAULT_BRANCH_WORKING_START,
      workingEndTime: DEFAULT_BRANCH_WORKING_END
    };
  }

  return {
    startMinutes,
    endMinutes,
    workingStartTime: toTimeText(startMinutes),
    workingEndTime: toTimeText(endMinutes)
  };
};

const buildTimeSlots = ({ startMinutes, endMinutes }) => {
  const safeStart = Number.isFinite(startMinutes) ? startMinutes : toMinutes(DEFAULT_BRANCH_WORKING_START);
  const safeEnd = Number.isFinite(endMinutes) ? endMinutes : toMinutes(DEFAULT_BRANCH_WORKING_END);
  const slots = [];
  for (let mins = safeStart; mins <= safeEnd - 15; mins += 15) {
    slots.push(toTimeText(mins));
  }
  return slots;
};

// ============ Main Component ============

export default function Appointment({ onLogout, currentUser }) {
  const defaultTodayIso = new Date().toISOString().slice(0, 10);
  const createEmptyForm = ({ date = "", start = "", member = "", memberPublicId = "", branchPublicId = "", followUpSourceRef = "" } = {}) => ({
    date,
    branchPublicId,
    member,
    memberPublicId,
    followUpSourceRef,
    customer: "",
    contact: "",
    services: [],
    serviceAmounts: {},
    serviceMembers: {},
    serviceStatuses: {},
    isWalkIn: false,
    totalDuration: 0,
    expectedTotal: 0,
    start,
    end: ""
  });

  const [form, setForm] = useState({
    date: "",
    branchPublicId: "",
    member: "",
    memberPublicId: "",
    followUpSourceRef: "",
    customer: "",
    contact: "",
    services: [],
    serviceAmounts: {},
    serviceMembers: {},
    serviceStatuses: {},
    isWalkIn: false,
    totalDuration: 0,
    expectedTotal: 0,
    start: "",
    end: ""
  });
  const [logs, setLogs] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [filters, setFilters] = useState({
    teamMember: "",
    branchPublicId: "",
    date: defaultTodayIso,
    time: "",
    customerName: "",
    status: "pending"
  });
  
  const [view, setView] = useState("appointments");
  const [theme, setTheme] = useState(() => normalizeTheme(localStorage.getItem("dw-theme") || "dark"));
  const [teamMembers, setTeamMembers] = useState([]);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [branches, setBranches] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [pendingConversion, setPendingConversion] = useState(null);
  const [initialFormSnapshot, setInitialFormSnapshot] = useState("");

  const buildFormSnapshot = value => {
    const activeServices = Array.isArray(value?.services) ? value.services : [];
    const normalizedRefs = activeServices.map(ref => String(ref || "")).filter(Boolean);
    return JSON.stringify({
      date: String(value?.date || ""),
      branchPublicId: String(value?.branchPublicId || ""),
      member: String(value?.member || ""),
      memberPublicId: String(value?.memberPublicId || ""),
      followUpSourceRef: String(value?.followUpSourceRef || ""),
      customer: String(value?.customer || "").trim(),
      contact: String(value?.contact || "").trim(),
      services: normalizedRefs,
      serviceAmounts: normalizedRefs.reduce((acc, ref) => {
        acc[ref] = Number(value?.serviceAmounts?.[ref] ?? 0);
        return acc;
      }, {}),
      serviceMembers: normalizedRefs.reduce((acc, ref) => {
        acc[ref] = String(value?.serviceMembers?.[ref] || "").trim();
        return acc;
      }, {}),
      serviceStatuses: normalizedRefs.reduce((acc, ref) => {
        acc[ref] = String(value?.serviceStatuses?.[ref] || "pending");
        return acc;
      }, {}),
      isWalkIn: !!value?.isWalkIn,
      start: String(value?.start || ""),
      end: String(value?.end || "")
    });
  };

  const availableMenus = useMemo(() => (
    currentUser?.role === "admin"
      ? [...ALL_MENU_KEYS]
      : (Array.isArray(currentUser?.allowedMenus) && currentUser.allowedMenus.length
        ? currentUser.allowedMenus
        : [...TEAM_MEMBER_DEFAULT_MENUS])
  ), [currentUser]);

  useEffect(() => {
    document.body.classList.remove("theme-dark", "theme-light", "theme-white");
    document.body.classList.add(`theme-${theme}`);
    localStorage.setItem("dw-theme", theme);
  }, [theme]);

  const nextTheme = getNextTheme(theme);

  // update view based on URL hash (simple routing)
  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash.replace("#", "") || "appointments";
      if (availableMenus.includes(hash)) {
        setView(hash);
      } else {
        const fallback = availableMenus.includes("appointments") ? "appointments" : availableMenus[0] || "appointments";
        window.location.hash = fallback;
        setView(fallback);
      }
    };
    window.addEventListener("hashchange", handleHash);
    handleHash();
    return () => window.removeEventListener("hashchange", handleHash);
  }, [availableMenus]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 900) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const members = useMemo(() => {
    return teamMembers.map(member => {
      const fullName = `${member.firstName || ""} ${member.lastName || ""}`.trim() || "Team Member";
      const avatar = getTeamMemberAvatar(member);
      return { name: fullName, avatar, color: member.calendarColor || "#bfa58a" };
    });
  }, [teamMembers]);

  const memberPublicIdByName = useMemo(() => {
    const map = new Map();
    teamMembers.forEach(member => {
      const name = `${member.firstName || ""} ${member.lastName || ""}`.trim();
      if (name && member.publicId) map.set(name, member.publicId);
    });
    return map;
  }, [teamMembers]);
  const memberNameByPublicId = useMemo(() => {
    const map = new Map();
    teamMembers.forEach(member => {
      const id = String(member.publicId || member.id || "").trim();
      const name = `${member.firstName || ""} ${member.lastName || ""}`.trim();
      if (id && name) map.set(id, name);
    });
    return map;
  }, [teamMembers]);
  const branchByPublicId = useMemo(() => {
    const map = new Map();
    branches.forEach(branch => {
      const publicId = String(branch.publicId || branch.id || "").trim();
      if (publicId) map.set(publicId, branch);
    });
    return map;
  }, [branches]);
  const memberByPublicId = useMemo(() => {
    const map = new Map();
    teamMembers.forEach(member => {
      const publicId = String(member.publicId || member.id || "").trim();
      if (publicId) map.set(publicId, member);
    });
    return map;
  }, [teamMembers]);
  const defaultAllowedBranchPublicId = Array.isArray(currentUser?.allowedBranchPublicIds) && currentUser.allowedBranchPublicIds.length === 1
    ? String(currentUser.allowedBranchPublicIds[0] || "")
    : "";
  const matchesBranchLocation = (member, branchPublicId) => {
    const branchId = String(branchPublicId || "").trim();
    if (!branchId) return true;
    const locations = Array.isArray(member?.locations) ? member.locations : [];
    return !locations.length || locations.includes(branchId);
  };
  const isMemberAvailableInBranch = useCallback((memberPublicId, branchPublicId) => {
    const member = memberByPublicId.get(String(memberPublicId || "").trim());
    if (!member) return false;
    return matchesBranchLocation(member, branchPublicId);
  }, [memberByPublicId]);
  const hasLinkedTeamMember = !!String(currentUser?.teamMemberPublicId || currentUser?.teamMemberName || "").trim();
  const ownMemberName = String(currentUser?.teamMemberName || "").trim();
  const teamOptions = useMemo(() => {
    const branchPublicId = String(filters.branchPublicId || "").trim();
    if (currentUser?.role === "team_member" && hasLinkedTeamMember && ownMemberName) {
      const ownMemberPublicId = String(currentUser?.teamMemberPublicId || memberPublicIdByName.get(ownMemberName) || "").trim();
      if (branchPublicId && ownMemberPublicId && !isMemberAvailableInBranch(ownMemberPublicId, branchPublicId)) {
        return [];
      }
      return [ownMemberName];
    }
    return teamMembers
      .filter(member => matchesBranchLocation(member, branchPublicId))
      .map(member => `${member.firstName || ""} ${member.lastName || ""}`.trim())
      .filter(Boolean);
  }, [currentUser?.role, currentUser?.teamMemberPublicId, filters.branchPublicId, hasLinkedTeamMember, isMemberAvailableInBranch, memberPublicIdByName, ownMemberName, teamMembers]);
  const formTeamMemberOptions = useMemo(() => {
    const branchPublicId = String(form.branchPublicId || filters.branchPublicId || defaultAllowedBranchPublicId || "").trim();
    return teamMembers
      .map(member => {
        const publicId = String(member.publicId || member.id || "").trim();
        const name = `${member.firstName || ""} ${member.lastName || ""}`.trim();
        return { publicId, name, member };
      })
      .filter(option => {
        if (!option.publicId || !option.name) return false;
        if (!matchesBranchLocation(option.member, branchPublicId)) return false;
        if (currentUser?.role === "team_member" && hasLinkedTeamMember && ownMemberName) {
          return option.name === ownMemberName;
        }
        return true;
      })
      .map(({ publicId, name }) => ({ publicId, name }));
  }, [currentUser?.role, defaultAllowedBranchPublicId, filters.branchPublicId, form.branchPublicId, hasLinkedTeamMember, ownMemberName, teamMembers]);

  const scheduleWindow = useMemo(() => {
    const selectedBranchPublicId = String(filters.branchPublicId || form.branchPublicId || "").trim();
    if (selectedBranchPublicId && branchByPublicId.has(selectedBranchPublicId)) {
      return getBranchWorkingWindow(branchByPublicId.get(selectedBranchPublicId));
    }

    if (branches.length) {
      return branches.reduce((acc, branch) => {
        const window = getBranchWorkingWindow(branch);
        if (!acc) return window;
        return {
          startMinutes: Math.min(acc.startMinutes, window.startMinutes),
          endMinutes: Math.max(acc.endMinutes, window.endMinutes),
          workingStartTime: toTimeText(Math.min(acc.startMinutes, window.startMinutes)),
          workingEndTime: toTimeText(Math.max(acc.endMinutes, window.endMinutes))
        };
      }, null) || getBranchWorkingWindow(null);
    }

    return getBranchWorkingWindow(null);
  }, [branchByPublicId, branches, filters.branchPublicId, form.branchPublicId]);

  const timeSlots = useMemo(() => buildTimeSlots(scheduleWindow), [scheduleWindow]);
  const todayIso = defaultTodayIso;
  const resolvePreferredBranchPublicId = useCallback(() => {
    if (filters.branchPublicId) return String(filters.branchPublicId || "").trim();
    if (defaultAllowedBranchPublicId) return defaultAllowedBranchPublicId;
    if (branches.length === 1) return String(branches[0]?.publicId || "").trim();
    return "";
  }, [branches, defaultAllowedBranchPublicId, filters.branchPublicId]);

  const openExistingAppointmentForEdit = (sourceEntry, fallback = {}) => {
    const getServiceRef = item => String(item?.ref || item?.publicId || item?.id || "").trim();
    const parsedServices = (() => {
      if (!sourceEntry?.servicesJson) return [];
      try {
        const parsed = JSON.parse(sourceEntry.servicesJson);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    })();
    const serviceRefs = parsedServices
      .map(item => getServiceRef(item))
      .filter(Boolean);
    const serviceAmounts = parsedServices.reduce((acc, item) => {
      const ref = getServiceRef(item);
      if (ref) acc[ref] = Number(item.amount || 0);
      return acc;
    }, {});
    const serviceMembers = parsedServices.reduce((acc, item) => {
      const ref = getServiceRef(item);
      if (ref) {
        acc[ref] = String(item.memberPublicId || sourceEntry.memberPublicId || "");
      }
      return acc;
    }, {});
    const serviceStatuses = parsedServices.reduce((acc, item) => {
      const ref = getServiceRef(item);
      if (ref) acc[ref] = item.status || "pending";
      return acc;
    }, {});

    const nextForm = {
      ...createEmptyForm(),
      date: sourceEntry.date || fallback.date || "",
      branchPublicId: String(sourceEntry.branchPublicId || fallback.branchPublicId || resolvePreferredBranchPublicId()),
      start: sourceEntry.start || fallback.time || "",
      end: sourceEntry.end || "",
      member: sourceEntry.member || "",
      memberPublicId: sourceEntry.memberPublicId || memberPublicIdByName.get(sourceEntry.member) || "",
      followUpSourceRef: String(sourceEntry.followUpSourceRef || "").trim(),
      customer: sourceEntry.customer || "",
      contact: sourceEntry.contact || "",
      isWalkIn: !!sourceEntry.isWalkIn,
      services: serviceRefs,
      serviceAmounts,
      serviceMembers,
      serviceStatuses
    };
    setEditingAppointment(sourceEntry);
    setForm(nextForm);
    setInitialFormSnapshot(buildFormSnapshot(nextForm));
    setPanelOpen(true);
  };

  const openSyncedAppointmentFromDashboard = async ({ workLogRef, appointmentDate } = {}) => {
    const targetRef = String(workLogRef || "").trim();
    if (!targetRef) return;

    window.location.hash = "appointments";
    setView("appointments");
    if (appointmentDate) {
      setFilters(prev => ({ ...prev, date: appointmentDate, status: "all" }));
    }

    try {
      const workRes = await apiFetch("/api/work");
      if (!workRes.ok) {
        throw new Error("Could not refresh appointments");
      }
      const workData = await workRes.json();
      const nextLogs = Array.isArray(workData) ? workData : [];
      setLogs(nextLogs);

      const sourceEntry = nextLogs.find(item => String(item.publicId || item.id || "") === targetRef);
      if (!sourceEntry) {
        alert("Approved successfully, but synced appointment record was not found in the current appointment list.");
        return;
      }
      if (sourceEntry.branchPublicId) {
        setFilters(prev => ({ ...prev, branchPublicId: String(sourceEntry.branchPublicId || ""), date: sourceEntry.date || prev.date, status: "all" }));
      }
      if (!canDo(currentUser, "appointments", "edit")) {
        alert("Approved successfully. You can track it from Appointment Management, but your role does not allow opening it in edit mode.");
        return;
      }
      openExistingAppointmentForEdit(sourceEntry, { date: sourceEntry.date, time: sourceEntry.start });
    } catch (err) {
      console.error("Failed to open synced appointment", err);
      alert("Approved successfully, but could not open the synced appointment automatically.");
    }
  };

  useEffect(() => {
    const loadAppointmentData = async () => {
      try {
        const [workRes, teamRes, servicesRes, clientsRes, branchesRes] = await Promise.all([
          apiFetch("/api/work"),
          apiFetch("/api/team-members"),
          apiFetch("/api/services"),
          apiFetch("/api/clients"),
          apiFetch("/api/branches")
        ]);

        const workData = await workRes.json();
        const teamData = await teamRes.json();
        const servicesData = await servicesRes.json();
        const clientsData = await clientsRes.json();
        const branchesData = await branchesRes.json();

        setLogs(Array.isArray(workData) ? workData : []);
        setTeamMembers(Array.isArray(teamData) ? teamData : []);
        setServiceCatalog(Array.isArray(servicesData) ? servicesData : []);
        setClients(Array.isArray(clientsData) ? clientsData : []);
        setBranches(Array.isArray(branchesData) ? branchesData : []);
      } catch (err) {
        console.error("Failed to load appointment data", err);
      }
    };

    loadAppointmentData();
  }, []);

  useEffect(() => {
    const preferredBranchPublicId = resolvePreferredBranchPublicId();
    if (!preferredBranchPublicId) return;

    setFilters(prev => (prev.branchPublicId ? prev : { ...prev, branchPublicId: preferredBranchPublicId }));
    setForm(prev => (prev.branchPublicId ? prev : { ...prev, branchPublicId: preferredBranchPublicId }));
  }, [branches, defaultAllowedBranchPublicId, resolvePreferredBranchPublicId]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    if (name === "member") {
      setForm(prev => ({
        ...prev,
        member: value,
        memberPublicId: memberPublicIdByName.get(value) || prev.memberPublicId || ""
      }));
      return;
    }
    if (name === "branchPublicId") {
      setForm(prev => {
        const nextBranchPublicId = String(value || "").trim();
        const selectedMemberAllowed = !prev.memberPublicId || isMemberAvailableInBranch(prev.memberPublicId, nextBranchPublicId);
        const nextServiceMembers = Object.keys(prev.serviceMembers || {}).reduce((acc, key) => {
          const serviceMemberPublicId = String(prev.serviceMembers?.[key] || "").trim();
          acc[key] = !serviceMemberPublicId || isMemberAvailableInBranch(serviceMemberPublicId, nextBranchPublicId)
            ? serviceMemberPublicId
            : "";
          return acc;
        }, {});

        return {
          ...prev,
          branchPublicId: nextBranchPublicId,
          member: selectedMemberAllowed ? prev.member : "",
          memberPublicId: selectedMemberAllowed ? prev.memberPublicId : "",
          serviceMembers: nextServiceMembers
        };
      });
      return;
    }
    if (name === "isWalkIn") {
      setForm(prev => ({
        ...prev,
        isWalkIn: checked,
        customer: checked ? "" : prev.customer,
        contact: checked ? "" : prev.contact
      }));
      return;
    }
    setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const addServiceToForm = serviceRef => {
    if (!serviceRef) return;
    setForm(prev => {
      if (prev.services.includes(serviceRef)) return prev;
      const serviceRow = serviceCatalog.find(service => (service.publicId || String(service.id)) === serviceRef);
      const defaultAmount = Number(serviceRow?.price || 0);
      return {
        ...prev,
        services: [...prev.services, serviceRef],
        serviceAmounts: {
          ...prev.serviceAmounts,
          [serviceRef]: defaultAmount
        },
        serviceMembers: {
          ...prev.serviceMembers,
          [serviceRef]: prev.serviceMembers?.[serviceRef]
            || prev.memberPublicId
            || (currentUser?.teamMemberPublicId || "")
            || formTeamMemberOptions[0]?.publicId
            || ""
        },
        serviceStatuses: {
          ...prev.serviceStatuses,
          [serviceRef]: prev.serviceStatuses?.[serviceRef] || "pending"
        }
      };
    });
  };

  const removeServiceFromForm = serviceRef => {
    setForm(prev => ({
      ...prev,
      services: prev.services.filter(ref => ref !== serviceRef),
      serviceAmounts: Object.keys(prev.serviceAmounts).reduce((acc, key) => {
        if (key !== serviceRef) acc[key] = prev.serviceAmounts[key];
        return acc;
      }, {}),
      serviceMembers: Object.keys(prev.serviceMembers || {}).reduce((acc, key) => {
        if (key !== serviceRef) acc[key] = prev.serviceMembers[key];
        return acc;
      }, {}),
      serviceStatuses: Object.keys(prev.serviceStatuses || {}).reduce((acc, key) => {
        if (key !== serviceRef) acc[key] = prev.serviceStatuses[key];
        return acc;
      }, {})
    }));
  };

  const updateServiceAmount = (serviceRef, amount) => {
    const numericAmount = Number(amount);
    setForm(prev => ({
      ...prev,
      serviceAmounts: {
        ...prev.serviceAmounts,
        [serviceRef]: Number.isFinite(numericAmount) ? numericAmount : 0
      }
    }));
  };

  const updateServiceMember = (serviceRef, memberRef) => {
    setForm(prev => ({
      ...prev,
      serviceMembers: {
        ...prev.serviceMembers,
        [serviceRef]: String(memberRef || "")
      }
    }));
  };

  const setServiceStatus = (serviceRef, nextStatus) => {
    setForm(prev => {
      const resolvedStatus = nextStatus === "done"
        ? "done"
        : (nextStatus === "carry_forward" ? "carry_forward" : "pending");
      return {
        ...prev,
        serviceStatuses: {
          ...prev.serviceStatuses,
          [serviceRef]: resolvedStatus
        }
      };
    });
  };

  const selectCustomer = client => {
    const fullName = `${client.firstName || ""} ${client.lastName || ""}`.trim();
    setForm(prev => ({
      ...prev,
      customer: fullName || prev.customer,
      contact: client.mobile || prev.contact
    }));
  };

  const findMatchingClient = ({ customerName, customerContact }) => {
    const targetName = String(customerName || "").trim().toLowerCase();
    const targetContact = String(customerContact || "").trim().toLowerCase();
    return clients.find(client => {
      const fullName = `${client.firstName || ""} ${client.lastName || ""}`.trim().toLowerCase();
      const mobile = String(client.mobile || "").trim().toLowerCase();
      return (targetName && fullName === targetName) || (targetContact && mobile === targetContact);
    }) || null;
  };

  useEffect(() => {
    const selectedServiceRows = form.services
      .map(ref => serviceCatalog.find(service => (service.publicId || String(service.id)) === ref))
      .filter(Boolean);
    const totalDuration = selectedServiceRows.reduce(
      (sum, service) => sum + Math.max(0, Number(service.durationMinutes || 30)),
      0
    );
    const end = form.start && totalDuration > 0 ? addMinutesToTime(form.start, totalDuration) : "";
    const expectedTotal = selectedServiceRows.reduce((sum, service) => {
      const ref = service.publicId || String(service.id);
      const editableAmount = Number(form.serviceAmounts?.[ref]);
      const fallback = Number(service.price || 0);
      return sum + (Number.isFinite(editableAmount) ? editableAmount : fallback);
    }, 0);

    setForm(prev => {
      if (prev.totalDuration === totalDuration && prev.end === end && prev.expectedTotal === expectedTotal) return prev;
      return { ...prev, totalDuration, expectedTotal, end };
    });
  }, [form.services, form.start, form.serviceAmounts, serviceCatalog]);

  const selectedServices = useMemo(() => {
    return form.services
      .map(ref => serviceCatalog.find(service => (service.publicId || String(service.id)) === ref))
      .filter(Boolean);
  }, [form.services, serviceCatalog]);

  const calendarEntries = useMemo(() => {
    return logs.flatMap(log => {
      const parsedServices = (() => {
        if (!log?.servicesJson) return [];
        try {
          const parsed = JSON.parse(log.servicesJson);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      })();

      if (!parsedServices.length) {
        return [{ ...log, sourceAppointmentRef: log.publicId || log.id }];
      }

      const groupedByMember = new Map();
      parsedServices.forEach(service => {
        const serviceMemberPublicId = String(service.memberPublicId || log.memberPublicId || "").trim();
        const serviceMemberName = String(
          service.memberName
          || memberNameByPublicId.get(serviceMemberPublicId)
          || log.member
          || ""
        ).trim();
        const groupKey = `${serviceMemberPublicId}::${serviceMemberName || "unassigned"}`;
        if (!groupedByMember.has(groupKey)) {
          groupedByMember.set(groupKey, {
            member: serviceMemberName || String(log.member || "").trim(),
            memberPublicId: serviceMemberPublicId,
            services: []
          });
        }
        groupedByMember.get(groupKey).services.push(service);
      });

      if (!groupedByMember.size) {
        return [{ ...log, sourceAppointmentRef: log.publicId || log.id }];
      }

      return Array.from(groupedByMember.values()).map(group => {
        const groupedServiceNames = group.services.map(service => service.name).filter(Boolean);
        const groupedDuration = group.services.reduce((sum, service) => sum + Math.max(0, Number(service.durationMinutes || 0)), 0);
        const groupedAmount = group.services.reduce((sum, service) => sum + Math.max(0, Number(service.amount || 0)), 0);
        return {
          ...log,
          member: group.member,
          memberPublicId: group.memberPublicId || log.memberPublicId,
          service: groupedServiceNames.join(", ") || log.service,
          servicesJson: JSON.stringify(group.services),
          totalDuration: groupedDuration || log.totalDuration,
          totalAmount: groupedAmount || log.totalAmount,
          sourceAppointmentRef: log.publicId || log.id
        };
      });
    });
  }, [logs, memberNameByPublicId]);

  const handleFilterChange = e => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const getFilteredLogs = () => {
    return calendarEntries.filter(log => {
      const customerText = String(log.customer || "");
      const contactText = String(log.contact || "");
      const customerQuery = String(filters.customerName || "").trim().toLowerCase();
      const matchesBranch = !filters.branchPublicId || String(log.branchPublicId || "") === String(filters.branchPublicId || "");
      const matchesMember = !filters.teamMember || log.member === filters.teamMember;
      const matchesDate = !filters.date || log.date === filters.date;
      const matchesTime = !filters.time || log.start === filters.time;
      const matchesCustomer = !customerQuery
        || customerText.toLowerCase().includes(customerQuery)
        || contactText.toLowerCase().includes(customerQuery);
      const statusFilter = String(filters.status || "pending").trim().toLowerCase();
      const isInvoiced = !!log.convertedToSaleRef;
      const isClosed = Number(log.closedWithoutInvoice || 0) === 1;
      const isFollowUp = !!String(log.followUpSourceRef || "").trim();
      const matchesStatus = (() => {
        if (statusFilter === "all") return true;
        if (statusFilter === "invoiced") return isInvoiced;
        if (statusFilter === "closed") return isClosed;
        if (statusFilter === "follow_up") return isFollowUp;
        return !isInvoiced && !isClosed;
      })();
      return matchesBranch && matchesMember && matchesDate && matchesTime && matchesCustomer && matchesStatus;
    });
  };

  const getVisibleTeamMembers = () => {
    if (!filters.teamMember) return teamOptions;
    return teamOptions.filter(m => m === filters.teamMember);
  };

  const clearFilters = () => {
    setFilters({
      teamMember: "",
      branchPublicId: defaultAllowedBranchPublicId || (branches.length === 1 ? String(branches[0]?.publicId || "") : ""),
      date: defaultTodayIso,
      time: "",
      customerName: "",
      status: "pending"
    });
  };

  const openPanel = () => {
    setEditingAppointment(null);
    setSelectedSlot(null);
    const defaultDate = filters.date || todayIso;
    const preferredBranchPublicId = resolvePreferredBranchPublicId();
    if (currentUser?.role === "team_member" && hasLinkedTeamMember && ownMemberName) {
      const nextForm = createEmptyForm({
        date: defaultDate,
        branchPublicId: preferredBranchPublicId,
        member: ownMemberName,
        memberPublicId: currentUser.teamMemberPublicId || memberPublicIdByName.get(ownMemberName) || ""
      });
      setForm(nextForm);
      setInitialFormSnapshot(buildFormSnapshot(nextForm));
    } else {
      const nextForm = createEmptyForm({ date: defaultDate, branchPublicId: preferredBranchPublicId });
      setForm(nextForm);
      setInitialFormSnapshot(buildFormSnapshot(nextForm));
    }
    setPanelOpen(true);
  };

  const handleSlotSelect = slot => {
    setSelectedSlot(slot);
    if (slot.entry) {
      if (!canDo(currentUser, "appointments", "edit")) return;
      const sourceEntryRef = String(slot.entry.sourceAppointmentRef || "").trim();
      const sourceEntry = sourceEntryRef
        ? (logs.find(item => String(item.publicId || item.id) === sourceEntryRef) || slot.entry)
        : slot.entry;
      openExistingAppointmentForEdit(sourceEntry, { date: slot.date, time: slot.time });
      return;
    }

    setEditingAppointment(null);
    const nextMember = currentUser?.role === "team_member" && hasLinkedTeamMember
      ? ownMemberName
      : (slot.member || "");
    const nextMemberPublicId = currentUser?.role === "team_member" && hasLinkedTeamMember
      ? (currentUser.teamMemberPublicId || memberPublicIdByName.get(ownMemberName) || "")
      : (memberPublicIdByName.get(nextMember) || "");
    const nextForm = createEmptyForm({
      date: slot.date,
      branchPublicId: resolvePreferredBranchPublicId(),
      start: slot.time,
      member: nextMember,
      memberPublicId: nextMemberPublicId
    });
    setForm(nextForm);
    setInitialFormSnapshot(buildFormSnapshot(nextForm));
    setPanelOpen(true);
  };

  const closePanel = forceOrEvent => {
    const force = typeof forceOrEvent === "boolean" ? forceOrEvent : false;
    if (!force && panelOpen) {
      const hasChanges = initialFormSnapshot && buildFormSnapshot(form) !== initialFormSnapshot;
      if (hasChanges) {
        const shouldClose = window.confirm("You are closing without saving the details. Continue?");
        if (!shouldClose) return;
      }
    }
    setPanelOpen(false);
    setEditingAppointment(null);
    setInitialFormSnapshot("");
  };

  const submit = async e => {
    e.preventDefault();

    if (!form.branchPublicId) {
      alert("Please select a branch.");
      return;
    }

    if (!form.isWalkIn && !form.customer.trim()) {
      alert("Please enter customer details or mark this appointment as walk-in.");
      return;
    }

    if (!form.services.length) {
      alert("Please select at least one service.");
      return;
    }

    const unassignedService = form.services.find(ref => !String(form.serviceMembers?.[ref] || "").trim());
    if (unassignedService) {
      alert("Please assign a team member for each selected service.");
      return;
    }

    if (!form.end) {
      alert("Unable to calculate end time. Please select a start slot and services.");
      return;
    }

    const payloadServices = form.services.map(ref => {
      const memberRef = String(form.serviceMembers?.[ref] || "").trim();
      return {
        ref,
        amount: Number(form.serviceAmounts?.[ref] ?? 0),
        memberPublicId: memberRef,
        memberName: memberNameByPublicId.get(memberRef) || "",
        status: form.serviceStatuses?.[ref] || "pending"
      };
    });
    const leadService = payloadServices[0] || null;
    const leadMemberPublicId = leadService?.memberPublicId || form.memberPublicId || "";
    const leadMemberName = memberNameByPublicId.get(leadMemberPublicId) || form.member || "";

    const isEditMode = !!editingAppointment;
    const endpoint = isEditMode ? `/api/work/${editingAppointment.publicId || editingAppointment.id}` : "/api/work";
    const res = await apiFetch(endpoint, {
      method: isEditMode ? "PUT" : "POST",
      body: JSON.stringify({
        date: form.date,
        branchPublicId: form.branchPublicId,
        member: leadMemberName,
        memberPublicId: leadMemberPublicId,
        customer: form.customer,
        contact: form.contact,
        services: payloadServices,
        isWalkIn: form.isWalkIn,
        followUpSourceRef: form.followUpSourceRef || "",
        start: form.start,
        end: form.end
      })
    });

    if (res.status === 401) {
      clearAuthSession();
      onLogout();
      return;
    }

    if (!res.ok) {
      const errorBody = await res.json().catch(() => ({}));
      alert(errorBody.error || "Could not save appointment");
      return;
    }

    const saved = await res.json();
    if (isEditMode) {
      setLogs(prev => prev.map(item => (item.id === saved.id ? saved : item)));
    } else {
      setLogs([saved, ...logs]);
    }
    closePanel(true);
    setForm(createEmptyForm({ branchPublicId: resolvePreferredBranchPublicId() }));
  };

  const handleConvertToInvoice = async () => {
    if (!editingAppointment) return;

    const serviceNameByRef = new Map(
      serviceCatalog.map(item => [item.publicId || String(item.id), item.name || "Service"])
    );
    const invoiceServiceRefs = [];
    const pendingServiceNames = [];

    form.services.forEach(ref => {
      const status = String(form.serviceStatuses?.[ref] || "pending").toLowerCase();
      const serviceName = serviceNameByRef.get(ref) || "Service";
      if (status === "done" || status === "carry_forward") {
        invoiceServiceRefs.push(ref);
      } else {
        pendingServiceNames.push(serviceName);
      }
    });

    if (!invoiceServiceRefs.length) {
      alert("No invoice-ready services found. Mark at least one service as Done or Carry Forward.");
      return;
    }

    if (pendingServiceNames.length) {
      const uniquePending = Array.from(new Set(pendingServiceNames));
      const message = [
        "These services are still pending:",
        ...uniquePending.map(name => `- ${name}`),
        "",
        "Only Done and Carry Forward services will be converted to invoice.",
        "Do you want to continue?"
      ].join("\n");
      const shouldContinue = window.confirm(message);
      if (!shouldContinue) return;
    }

    const payloadServices = form.services.map(ref => {
      const memberRef = String(form.serviceMembers?.[ref] || "").trim();
      return {
        ref,
        amount: Number(form.serviceAmounts?.[ref] ?? 0),
        memberPublicId: memberRef,
        memberName: memberNameByPublicId.get(memberRef) || "",
        status: form.serviceStatuses?.[ref] || "pending"
      };
    });
    const leadService = payloadServices[0] || null;
    const leadMemberPublicId = leadService?.memberPublicId || form.memberPublicId || "";
    const leadMemberName = memberNameByPublicId.get(leadMemberPublicId) || form.member || "";
    const ref = editingAppointment.publicId || editingAppointment.id;

    const saveRes = await apiFetch(`/api/work/${ref}`, {
      method: "PUT",
      body: JSON.stringify({
        date: form.date,
        branchPublicId: form.branchPublicId,
        member: leadMemberName,
        memberPublicId: leadMemberPublicId,
        customer: form.customer,
        contact: form.contact,
        services: payloadServices,
        isWalkIn: form.isWalkIn,
        followUpSourceRef: form.followUpSourceRef || "",
        start: form.start,
        end: form.end
      })
    });

    if (saveRes.status === 401) {
      clearAuthSession();
      onLogout();
      return;
    }

    if (!saveRes.ok) {
      const errorBody = await saveRes.json().catch(() => ({}));
      alert(errorBody.error || "Could not update appointment before conversion");
      return;
    }

    const savedAppointment = await saveRes.json();
    const savedRef = String(savedAppointment.publicId || savedAppointment.id || "");
    setLogs(prev => prev.map(item => (
      String(item.publicId || item.id || "") === savedRef ? savedAppointment : item
    )));

    const matchedClient = form.isWalkIn
      ? null
      : findMatchingClient({
          customerName: form.customer,
          customerContact: form.contact
        });

    const conversionData = {
      saleDate: form.date || todayIso,
      customerName: form.isWalkIn ? "" : form.customer,
      customerContact: form.isWalkIn ? "" : form.contact,
      customerPublicId: matchedClient?.publicId || "",
      branchPublicId: form.branchPublicId || matchedClient?.branchPublicId || defaultAllowedBranchPublicId,
      services: invoiceServiceRefs.map(ref => {
        const serviceRow = serviceCatalog.find(s => (s.publicId || String(s.id)) === ref);
        const memberRef = String(form.serviceMembers?.[ref] || "").trim();
        return {
          servicePublicId: ref,
          serviceName: serviceRow?.name || "",
          servicePrice: Number(form.serviceAmounts?.[ref] ?? serviceRow?.price ?? 0),
          memberPublicId: memberRef,
          memberName: memberNameByPublicId.get(memberRef) || ""
        };
      }),
      sourceRef: savedAppointment?.publicId || savedAppointment?.id || null
    };
    setPendingConversion(conversionData);
    closePanel(true);
    window.location.hash = "sales";
  };

  const createFollowUpAppointment = () => {
    const carryForwardServiceRefs = form.services.filter(ref => String(form.serviceStatuses?.[ref] || "pending").toLowerCase() === "carry_forward");
    if (!carryForwardServiceRefs.length) {
      alert("There are no carry-forward services left for a follow-up appointment.");
      return;
    }

    const nextDate = addDaysToIsoDate(form.date || todayIso, 1);
    const leadPendingRef = carryForwardServiceRefs[0] || "";
    const leadMemberPublicId = String(form.serviceMembers?.[leadPendingRef] || form.memberPublicId || "").trim();
    const nextForm = {
      ...createEmptyForm({
        date: nextDate,
        branchPublicId: form.branchPublicId || resolvePreferredBranchPublicId(),
        start: form.start || "",
        member: memberNameByPublicId.get(leadMemberPublicId) || form.member || "",
        memberPublicId: leadMemberPublicId,
        followUpSourceRef: String(form.followUpSourceRef || editingAppointment?.publicId || editingAppointment?.id || "").trim()
      }),
      customer: form.customer,
      contact: form.contact,
      isWalkIn: !!form.isWalkIn,
      services: carryForwardServiceRefs,
      serviceAmounts: carryForwardServiceRefs.reduce((acc, ref) => {
        acc[ref] = Number(form.serviceAmounts?.[ref] ?? 0);
        return acc;
      }, {}),
      serviceMembers: carryForwardServiceRefs.reduce((acc, ref) => {
        acc[ref] = String(form.serviceMembers?.[ref] || "").trim();
        return acc;
      }, {}),
      serviceStatuses: carryForwardServiceRefs.reduce((acc, ref) => {
        acc[ref] = "pending";
        return acc;
      }, {})
    };

    setEditingAppointment(null);
    setSelectedSlot(null);
    setForm(nextForm);
    setInitialFormSnapshot(buildFormSnapshot(nextForm));
    setPanelOpen(true);
  };

  const closePrepaidAppointment = async () => {
    if (!editingAppointment) return;

    const payloadServices = form.services.map(ref => {
      const memberRef = String(form.serviceMembers?.[ref] || "").trim();
      return {
        ref,
        amount: Number(form.serviceAmounts?.[ref] ?? 0),
        memberPublicId: memberRef,
        memberName: memberNameByPublicId.get(memberRef) || "",
        status: form.serviceStatuses?.[ref] || "pending"
      };
    });
    const leadService = payloadServices[0] || null;
    const leadMemberPublicId = leadService?.memberPublicId || form.memberPublicId || "";
    const leadMemberName = memberNameByPublicId.get(leadMemberPublicId) || form.member || "";
    const ref = editingAppointment.publicId || editingAppointment.id;

    const saveRes = await apiFetch(`/api/work/${ref}`, {
      method: "PUT",
      body: JSON.stringify({
        date: form.date,
        branchPublicId: form.branchPublicId,
        member: leadMemberName,
        memberPublicId: leadMemberPublicId,
        customer: form.customer,
        contact: form.contact,
        services: payloadServices,
        isWalkIn: form.isWalkIn,
        followUpSourceRef: form.followUpSourceRef || "",
        start: form.start,
        end: form.end
      })
    });

    if (saveRes.status === 401) {
      clearAuthSession();
      onLogout();
      return;
    }
    if (!saveRes.ok) {
      const errorBody = await saveRes.json().catch(() => ({}));
      alert(errorBody.error || "Could not update follow-up appointment before closing");
      return;
    }

    const closeRes = await apiFetch(`/api/work/${encodeURIComponent(ref)}/close`, { method: "PATCH" });
    if (closeRes.status === 401) {
      clearAuthSession();
      onLogout();
      return;
    }
    if (!closeRes.ok) {
      const errorBody = await closeRes.json().catch(() => ({}));
      alert(errorBody.error || "Could not close prepaid follow-up appointment");
      return;
    }

    const savedAppointment = await closeRes.json();
    const savedRef = String(savedAppointment.publicId || savedAppointment.id || "");
    setLogs(prev => prev.map(item => (
      String(item.publicId || item.id || "") === savedRef ? savedAppointment : item
    )));
    closePanel(true);
  };

  const deleteAppointment = async () => {
    if (!editingAppointment) return;
    if (!window.confirm("Delete this appointment?")) return;
    const ref = editingAppointment.publicId || editingAppointment.id;
    const res = await apiFetch(`/api/work/${ref}`, { method: "DELETE" });
    if (res.status === 401) { clearAuthSession(); onLogout(); return; }
    if (!res.ok) { alert("Could not delete appointment"); return; }
    setLogs(prev => prev.filter(item => (item.publicId || item.id) !== ref));
    closePanel(true);
  };

  const handleTeamMemberSaved = savedMember => {
    if (!savedMember) return;
    setTeamMembers(prev => {
      const exists = prev.some(member => String(member.publicId || member.id) === String(savedMember.publicId || savedMember.id));
      if (exists) {
        return prev.map(member => (String(member.publicId || member.id) === String(savedMember.publicId || savedMember.id) ? savedMember : member));
      }
      return [savedMember, ...prev];
    });
  };

  const handleTeamMemberDeleted = deletedId => {
    setTeamMembers(prev => prev.filter(member => String(member.publicId || member.id) !== String(deletedId)));
  };

  const handleAppointmentConverted = savedAppointment => {
    if (!savedAppointment) return;
    const savedRef = String(savedAppointment.publicId || savedAppointment.id || "");
    if (!savedRef) return;
    setLogs(prev => prev.map(item => (
      String(item.publicId || item.id || "") === savedRef ? savedAppointment : item
    )));
    setEditingAppointment(prev => (
      prev && String(prev.publicId || prev.id || "") === savedRef ? savedAppointment : prev
    ));
  };

  const isReadOnlyAppointment = !!(editingAppointment?.convertedToSaleRef || Number(editingAppointment?.closedWithoutInvoice || 0) === 1);
  const isPrepaidFollowUp = !!String(editingAppointment?.followUpSourceRef || form.followUpSourceRef || "").trim();
  const readOnlyMessage = editingAppointment?.convertedToSaleRef
    ? "Converted to Invoice - this appointment is read-only."
    : (Number(editingAppointment?.closedWithoutInvoice || 0) === 1
      ? "Closed Appointment - this prepaid follow-up is read-only."
      : "");

  return (
    <div className="appointment-shell">
      <div className="appointment-content">
      <Header
        company="SalonTechPro"
        user={`${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim() || currentUser?.username || "User"}
        roleLabel={currentUser?.role === "admin" ? "Admin" : "Team Member"}
        onLogout={onLogout}
        theme={theme}
        nextThemeLabel={THEME_BUTTON_LABELS[nextTheme]}
        onToggleTheme={() => setTheme(prev => getNextTheme(normalizeTheme(prev)))}
      />
      <button
        type="button"
        className="mobile-menu-btn"
        onClick={() => setIsMobileMenuOpen(true)}
        aria-label="Open navigation menu"
      >
        <span aria-hidden="true">&#9776;</span>
        <span>Menu</span>
      </button>
      <Sidebar
        availableMenus={availableMenus}
        isOpen={isMobileMenuOpen}
        onNavigate={() => setIsMobileMenuOpen(false)}
      />
      {isMobileMenuOpen && <div className="sidebar-overlay" onClick={() => setIsMobileMenuOpen(false)} />}
      
      {view === "services" ? (
        <Services currentUser={currentUser} />
      ) : view === "branches" ? (
        <Branches />
          ) : view === "dashboard" ? (
            <Dashboard currentUser={currentUser} onOpenSyncedAppointment={openSyncedAppointmentFromDashboard} />
      ) : view === "clients" ? (
        <Clients currentUser={currentUser} />
      ) : view === "whatsapp" ? (
        <WhatsAppInbox currentUser={currentUser} />
      ) : view === "staff" ? (
        <TeamMembers
          currentUser={currentUser}
          onMemberSaved={handleTeamMemberSaved}
          onMemberDeleted={handleTeamMemberDeleted}
        />
      ) : view === "inventory" ? (
        <div className="page main-content">
          <h1>Module Not Found</h1>
          <p>The Inventory module is hosted externally and could not be reached. Please try again or contact your administrator.</p>
        </div>
      ) : view === "sales" ? (
        <Sales
          currentUser={currentUser}
          pendingConversion={pendingConversion}
          onConversionDone={() => setPendingConversion(null)}
          onAppointmentConverted={handleAppointmentConverted}
        />
      ) : view === "purchase" ? (
        <div className="page main-content">
          <h1>Purchase Module Not Available</h1>
          <p>The Purchase module is not available in this environment yet.</p>
        </div>
      ) : view === "reports" ? (
        <Reports currentUser={currentUser} />
      ) : view === "users" ? (
        <UserManagement />
      ) : view === "settings" ? (
        <Settings />
      ) : view === "appointments" ? (
        <div className="page main-content">
          <div className="appointment-header">
            <h1>Appointment Management</h1>
            {canDo(currentUser, "appointments", "add") && (
              <AddWorkButton
                onAddWork={() => {
                  if (selectedSlot) {
                    handleSlotSelect({
                      date: selectedSlot.date,
                      time: selectedSlot.time,
                      member: selectedSlot.member
                    });
                    return;
                  }
                  openPanel();
                }}
              />
            )}
          </div>

          <FilterSection
            filters={filters}
            handleFilterChange={handleFilterChange}
            team={teamOptions}
            branches={branches}
            timeSlots={timeSlots}
            clearFilters={clearFilters}
          />

          <SidePanel isOpen={panelOpen} onClose={closePanel} className="panel-medium">
            <WorkForm
              form={form}
              handleChange={handleChange}
              onAddService={addServiceToForm}
              onRemoveService={removeServiceFromForm}
              onServiceAmountChange={updateServiceAmount}
              onServiceMemberChange={updateServiceMember}
              onServiceStatusChange={setServiceStatus}
              onConvertToInvoice={!!editingAppointment && !isReadOnlyAppointment && !isPrepaidFollowUp && canDo(currentUser, "sales", "add") ? handleConvertToInvoice : null}
              onCreateFollowUp={!!editingAppointment && !Number(editingAppointment?.closedWithoutInvoice || 0) && (editingAppointment?.convertedToSaleRef || editingAppointment?.followUpSourceRef) ? createFollowUpAppointment : null}
              onCloseAppointment={!!editingAppointment && !isReadOnlyAppointment && isPrepaidFollowUp ? closePrepaidAppointment : null}
              isReadOnly={isReadOnlyAppointment}
              readOnlyMessage={readOnlyMessage}
              onSelectCustomer={selectCustomer}
              isEditing={!!editingAppointment}
              branches={branches}
              teamMembers={formTeamMemberOptions}
              services={serviceCatalog}
              clients={clients}
              selectedServices={selectedServices}
              timeSlots={timeSlots}
              onSubmit={submit}
              onClose={closePanel}
              onDelete={deleteAppointment}
              canDelete={canDo(currentUser, "appointments", "delete")}
            />
          </SidePanel>

          <Calendar
            timeSlots={timeSlots}
            visibleMembers={getVisibleTeamMembers()}
            filteredLogs={getFilteredLogs()}
            members={members}
            selectedSlot={selectedSlot}
            activeDate={filters.date || form.date || todayIso}
            onSelectSlot={handleSlotSelect}
          />
        </div>
      ) : (
        <div className="page main-content">
          <h1>Access Restricted</h1>
          <p>This section is not enabled for your user profile.</p>
        </div>
      )}

      </div>
      <Footer />
    </div>
  );
}
